/**
 * RecentWorkCard.jsx — Feb 09 2026, Lane 2 (Subscriber Home restructure).
 *
 * Signed-in Home landing card #3. Shows the three most recently
 * generated activities as compact chips using the existing
 * LibraryRecentStrip pattern. Empty state carries forward the existing
 * "No activities yet / Generate an activity" treatment.
 *
 * `activities` prop comes straight from Dashboard.jsx state — already
 * fetched at page load, no additional network call.
 */
import { Link } from "react-router-dom";
import { Sparkles } from "lucide-react";
import LibraryRecentStrip from "../LibraryRecentStrip";
import TrilliumIcon from "../TrilliumIcon";

/** Normaliser copied verbatim from Dashboard.jsx `activityToRecentItem`
 *  so RecentWorkCard is self-contained. If the two diverge later this
 *  is the version Home uses. */
const _activityToRecentItem = (a) => {
  const ot = (a?.output_type || "").toLowerCase();
  const isTest = ot === "test" && a.linked_test_id;
  const meta = [
    a?.inputs?.grade
      ? String(a.inputs.grade).toLowerCase().startsWith("grade")
        ? a.inputs.grade
        : `Grade ${a.inputs.grade}`
      : "",
    a?.inputs?.subject || "",
    a?.inputs?.strand || "",
  ]
    .filter(Boolean)
    .join(" · ");
  let dateLabel = "";
  if (a?.created_at) {
    try {
      dateLabel = new Date(a.created_at).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      });
    } catch {
      dateLabel = "";
    }
  }
  return {
    id: a.id,
    title: a.activity_name || "Untitled",
    subtitle: meta,
    dateLabel,
    href: isTest ? `/tests/${encodeURIComponent(a.linked_test_id)}` : `/lesson/${encodeURIComponent(a.id)}`,
  };
};

const _recentN = (list, n = 3) =>
  [...(list || [])]
    .sort((a, b) => {
      const ta = a?.created_at ? Date.parse(a.created_at) : 0;
      const tb = b?.created_at ? Date.parse(b.created_at) : 0;
      return tb - ta;
    })
    .slice(0, n);

export default function RecentWorkCard({ activities = [] }) {
  const list = _recentN(activities, 3).map(_activityToRecentItem);

  return (
    <section data-testid="home-recent-work-card" className="ae-card p-6 md:p-7">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-lg bg-[#f4f5f7] border border-[#4a6984]/15 grid place-items-center">
            <Sparkles className="h-4 w-4 text-[#4a6984]" />
          </div>
          <span className="font-bold uppercase text-xs tracking-[0.25em] text-[#4a6984]/70">
            Recent work
          </span>
        </div>
        {list.length > 0 && (
          <Link
            to="/?view=library"
            data-testid="home-recent-work-see-all"
            className="text-xs font-bold uppercase tracking-[0.12em] text-[#4a6984]/70 hover:text-[#1c2d3d] transition-colors"
          >
            See all →
          </Link>
        )}
      </div>

      {list.length === 0 ? (
        <div
          data-testid="home-recent-work-empty"
          className="text-center py-6"
        >
          <TrilliumIcon size={22} className="text-[#1c2d3d] mx-auto mb-3" />
          <p className="text-sm text-[#4a6984]/80 font-medium">No activities yet.</p>
          <Link
            to="/generator"
            data-testid="home-recent-work-empty-cta"
            className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-[#1c2d3d] hover:text-[#4a6984] transition-colors"
          >
            Generate your first activity →
          </Link>
        </div>
      ) : (
        <LibraryRecentStrip items={list} testid="home-recent-work-strip" />
      )}
    </section>
  );
}
